import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Trash2,
  Send,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Pencil,
  Plus,
  Save,
  X,
  ChevronDown,
  ExternalLink,
  FileDown,
  Eye,
} from "lucide-react";
import { EmailSendHistoryCard } from "@/components/estimates/EmailSendHistoryCard";
import { PdfPreviewDialog } from "@/components/reports/PdfPreviewDialog";
import { downloadPdfFromBase64 } from "@/lib/report-export";
import {
  sendChangeOrderApprovalLink,
  generateChangeOrderPdf,
} from "@/utils/change-orders.functions";
import { cleanChangeOrderTitle, isCustomerRequestReason } from "@/lib/change-order-text";
import { ScopePhotosUploader, type ScopePhoto } from "@/components/ScopePhotosUploader";
import { hydrateScopePhotos, serializeScopePhotos } from "@/lib/scope-photos";

export const Route = createFileRoute("/_app/change-orders/$changeOrderId")({
  component: ChangeOrderDetailPage,
});

type Status = "draft" | "sent" | "approved" | "rejected";

type CO = {
  id: string;
  number: string;
  title: string;
  description: string | null;
  reason: string | null;
  status: Status;
  currency: string;
  tax_percent: number;
  subtotal: number;
  tax_amount: number;
  total: number;
  estimate_id: string;
  contract_id: string | null;
  company_id: string;
  scope_photos: unknown;
  estimates: {
    id: string;
    number: string;
    title: string;
    total: number;
    clients: { name: string; email: string | null } | null;
  } | null;
  contracts: {
    id: string;
    number: string | null;
    title: string | null;
    amount: number | null;
  } | null;
};

type Item = {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  sort_order: number;
};

type ApprovalToken = {
  id: string;
  token: string;
  status: "sent" | "approved" | "rejected" | "revoked";
  client_email: string | null;
  created_at: string;
  responded_at: string | null;
  response_notes: string | null;
  client_signature_data: string | null;
  client_signature_name: string | null;
};

const statusVariant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "outline",
  approved: "default",
  rejected: "destructive",
};

function safeLocale(language: string | undefined): string {
  if (!language) return "en-US";
  // Remove POSIX suffixes and normalize underscores so Intl accepts the tag
  return language.replace(/@.*$/, "").replace(/_/g, "-") || "en-US";
}

function ChangeOrderDetailPage() {
  const { changeOrderId } = Route.useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { t, i18n } = useTranslation();
  const isAdmin = hasRole("admin");

  const [co, setCO] = useState<CO | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [tokens, setTokens] = useState<ApprovalToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [approvalLinksOpen, setApprovalLinksOpen] = useState(true);

  // Send dialog
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [sending, setSending] = useState(false);

  // PDF
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editReason, setEditReason] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTaxPercent, setEditTaxPercent] = useState<number>(0);
  const [editItems, setEditItems] = useState<
    Array<{ id?: string; description: string; quantity: number; unit_price: number }>
  >([]);
  const [editScopePhotos, setEditScopePhotos] = useState<ScopePhoto[]>([]);

  const startEditing = () => {
    if (!co) return;
    setEditTitle(co.title);
    setEditReason(co.reason ?? "");
    setEditDescription(co.description ?? "");
    setEditTaxPercent(Number(co.tax_percent) || 0);
    setEditItems(
      items.map((it) => ({
        id: it.id,
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
      })),
    );
    setEditing(true);
    void hydrateScopePhotos("change-order-scope-photos", co.scope_photos).then(setEditScopePhotos);
  };
  const cancelEditing = () => setEditing(false);

  const updateEditItem = (
    idx: number,
    patch: Partial<{ description: string; quantity: number; unit_price: number }>,
  ) => setEditItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addEditItem = () =>
    setEditItems((p) => [...p, { description: "", quantity: 1, unit_price: 0 }]);
  const removeEditItem = (idx: number) =>
    setEditItems((p) => (p.length === 1 ? p : p.filter((_, i) => i !== idx)));

  const editSubtotal = editItems.reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  const editTaxAmount = (editSubtotal * (editTaxPercent || 0)) / 100;
  const editTotal = editSubtotal + editTaxAmount;

  const load = async () => {
    setLoading(true);
    const [{ data: coData, error: coErr }, { data: itemsData }, { data: tokensData }] =
      await Promise.all([
        supabase
          .from("change_orders")
          .select(
            "id, number, title, description, reason, status, currency, tax_percent, subtotal, tax_amount, total, estimate_id, contract_id, company_id, scope_photos, estimates(id, number, title, total, clients(name, email)), contracts(id, number, title, amount)",
          )
          .eq("id", changeOrderId)
          .maybeSingle(),
        supabase
          .from("change_order_items")
          .select("id, description, quantity, unit_price, total, sort_order")
          .eq("change_order_id", changeOrderId)
          .order("sort_order"),
        supabase
          .from("change_order_approval_tokens")
          .select(
            "id, token, status, client_email, created_at, responded_at, response_notes, client_signature_data, client_signature_name",
          )
          .eq("change_order_id", changeOrderId)
          .order("created_at", { ascending: false }),
      ]);
    if (coErr) toast.error(t("common.error"), { description: coErr.message });
    setCO((coData ?? null) as unknown as CO | null);
    setItems((itemsData ?? []) as Item[]);
    setTokens((tokensData ?? []) as ApprovalToken[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeOrderId]);

  const performSaveEdit = async (resetToDraft: boolean) => {
    if (!co) return;
    if (!editTitle.trim()) {
      toast.error(t("pages.changeOrders.detail.titleRequired"));
      return;
    }
    const cleanItems = editItems.filter((i) => i.description.trim());
    if (cleanItems.length === 0) {
      toast.error(t("pages.changeOrders.detail.addLineItem"));
      return;
    }
    setSavingEdit(true);
    try {
      const updates: {
        title: string;
        reason: string | null;
        description: string | null;
        tax_percent: number;
        scope_photos: ReturnType<typeof serializeScopePhotos>;
        status?: Status;
      } = {
        title: editTitle.trim(),
        reason: editReason.trim() || null,
        description: editDescription.trim() || null,
        tax_percent: editTaxPercent,
        scope_photos: serializeScopePhotos(editScopePhotos),
      };
      if (resetToDraft) updates.status = "draft";

      const { error: uErr } = await supabase.from("change_orders").update(updates).eq("id", co.id);
      if (uErr) throw uErr;

      const { error: dErr } = await supabase
        .from("change_order_items")
        .delete()
        .eq("change_order_id", co.id);
      if (dErr) throw dErr;

      const { error: insErr } = await supabase.from("change_order_items").insert(
        cleanItems.map((it, idx) => ({
          change_order_id: co.id,
          description: it.description.trim(),
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          sort_order: idx,
        })),
      );
      if (insErr) throw insErr;

      if (resetToDraft) {
        const { error: rErr } = await supabase
          .from("change_order_approval_tokens")
          .update({ status: "revoked" })
          .eq("change_order_id", co.id)
          .eq("status", "sent");
        if (rErr) console.error("Could not revoke pending tokens", rErr);
      }

      toast.success(
        resetToDraft
          ? t("pages.changeOrders.detail.saveSuccessReverted")
          : t("pages.changeOrders.detail.saveSuccess"),
      );
      setEditing(false);
      setConfirmReset(false);
      await load();
    } catch (e) {
      console.error("[performSaveEdit] failed", e);
      const msg =
        (e as { message?: string } | null)?.message || t("pages.changeOrders.detail.saveFailed");
      toast.error(t("pages.changeOrders.detail.saveFailed"), { description: msg });
    } finally {
      setSavingEdit(false);
    }
  };

  const saveEdit = () => {
    if (!co) return;
    if (co.status !== "draft") setConfirmReset(true);
    else performSaveEdit(false);
  };

  const updateStatus = async (status: Status) => {
    if (!co) return;
    const { error } = await supabase.from("change_orders").update({ status }).eq("id", co.id);
    if (error) {
      toast.error(t("pages.changeOrders.detail.statusFailed"), { description: error.message });
    } else {
      setCO({ ...co, status });
      toast.success(
        t("pages.changeOrders.detail.statusUpdated", { status: t(`statuses.${status}`) }),
      );
    }
  };

  const onDelete = async () => {
    if (!co) return;
    const { error } = await supabase.from("change_orders").delete().eq("id", co.id);
    if (error) {
      toast.error(t("pages.changeOrders.detail.deleteFailed"), { description: error.message });
    } else {
      toast.success(t("pages.changeOrders.detail.deleted"));
      navigate({ to: "/change-orders" });
    }
  };

  const openSendDialog = () => {
    setSendEmail(co?.estimates?.clients?.email ?? "");
    setSendMessage("");
    setSendOpen(true);
  };

  const sendApprovalLink = async () => {
    if (!co) return;
    const email = sendEmail.trim();
    if (!email) {
      toast.error(t("pages.changeOrders.detail.recipientRequired"));
      return;
    }
    setSending(true);
    try {
      const result = await sendChangeOrderApprovalLink({
        data: {
          change_order_id: co.id,
          recipient_email: email,
          message: sendMessage.trim() || undefined,
          app_origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });
      await supabase.from("change_orders").update({ status: "sent" }).eq("id", co.id);
      toast.success(t("pages.changeOrders.detail.linkSent"), {
        description: result?.approval_url
          ? t("pages.changeOrders.detail.linkSentDescQueuedCopied")
          : t("pages.changeOrders.detail.linkSentDescQueued"),
      });
      if (result?.approval_url && typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(result.approval_url).catch(() => {});
      }
      setSendOpen(false);
      load();
    } catch (e: unknown) {
      toast.error(t("pages.changeOrders.detail.sendFailed"), {
        description: (e as Error)?.message ?? t("common.error"),
      });
    } finally {
      setSending(false);
    }
  };

  const copyApprovalLink = async (token: string) => {
    const url = `${window.location.origin}/change-orders/approve/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("pages.changeOrders.detail.copyLinkSuccess"));
    } catch {
      toast.error(t("pages.changeOrders.detail.copyLinkFailed"), { description: url });
    }
  };

  const openAsClient = (token: string) => {
    window.open(`${window.location.origin}/change-orders/approve/${token}`, "_blank");
  };

  const revokeToken = async (id: string) => {
    const { error } = await supabase
      .from("change_order_approval_tokens")
      .update({ status: "revoked" })
      .eq("id", id);
    if (error) {
      toast.error(t("pages.changeOrders.detail.revokeFailed"), { description: error.message });
    } else {
      toast.success(t("pages.changeOrders.detail.linkRevoked"));
      load();
    }
  };

  const openPdfPreview = async () => {
    if (!co) return;
    setPdfLoading(true);
    try {
      const { pdfBase64, filename } = await generateChangeOrderPdf({
        data: { changeOrderId: co.id },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename || `change-order-${co.number}.pdf`);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(t("common.error"), {
        description: (e as Error)?.message ?? "",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!co) return;
    setPdfLoading(true);
    try {
      const { pdfBase64, filename } = await generateChangeOrderPdf({
        data: { changeOrderId: co.id },
      });
      downloadPdfFromBase64(filename || `change-order-${co.number}.pdf`, pdfBase64);
    } catch (e) {
      toast.error(t("common.error"), {
        description: (e as Error)?.message ?? "",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(pdfPreviewFilename, pdfPreviewBase64);
  };

  const formatMoney = (v: number) =>
    new Intl.NumberFormat(safeLocale(i18n.language), {
      style: "currency",
      currency: co?.currency || "USD",
    }).format(Number(v) || 0);

  const signedMoney = (v: number) => `${v >= 0 ? "+" : ""}${formatMoney(v)}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!co) {
    return (
      <div className="rounded-lg border border-border bg-card p-12 text-center">
        <p className="font-medium">{t("pages.changeOrders.detail.notFound")}</p>
        <Link
          to="/change-orders"
          className="mt-2 inline-block text-sm text-primary hover:underline"
        >
          {t("pages.changeOrders.detail.back")}
        </Link>
      </div>
    );
  }

  const displayTotal = editing ? editTotal : Number(co.total) || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/change-orders" })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{co.number}</span>
              <Badge variant={statusVariant[co.status]} className="capitalize">
                {t(`statuses.${co.status}`)}
              </Badge>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {cleanChangeOrderTitle(co.title)}
            </h1>
            {co.estimates && (
              <p className="text-xs text-muted-foreground">
                <Link
                  to="/estimates/$estimateId"
                  params={{ estimateId: co.estimates.id }}
                  className="hover:underline"
                >
                  {co.estimates.number}
                </Link>
                {" — "}
                {co.estimates.title}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button
                variant="outline"
                className="gap-2"
                onClick={cancelEditing}
                disabled={savingEdit}
              >
                <X className="h-4 w-4" />
                {t("common.cancel")}
              </Button>
              <Button className="gap-2" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {savingEdit
                  ? t("pages.changeOrders.detail.savingDots")
                  : t("pages.changeOrders.detail.saveChanges")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="gap-2" onClick={startEditing}>
                <Pencil className="h-4 w-4" />
                {t("common.edit")}
              </Button>
              <Button variant="outline" className="gap-2" onClick={openSendDialog}>
                <Send className="h-4 w-4" />
                {t("pages.changeOrders.detail.sendToClient")}
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={openPdfPreview}
                disabled={pdfLoading}
              >
                {pdfLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                {t("pages.changeOrders.detail.viewPdf")}
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={downloadPdf}
                disabled={pdfLoading}
              >
                <FileDown className="h-4 w-4" />
                {t("pages.changeOrders.detail.downloadPdf")}
              </Button>
              <Select value={co.status} onValueChange={(v) => updateStatus(v as Status)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t("statuses.draft")}</SelectItem>
                  <SelectItem value="sent">{t("statuses.sent")}</SelectItem>
                  <SelectItem value="approved">{t("statuses.approved")}</SelectItem>
                  <SelectItem value="rejected">{t("statuses.rejected")}</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && (
                <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {editing && co.status !== "draft" && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <p className="font-medium">
            {t("pages.changeOrders.detail.editingWarn", { status: t(`statuses.${co.status}`) })}
          </p>
          <p
            className="text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: t("pages.changeOrders.detail.editingWarnDesc") }}
          />
        </div>
      )}

      {tokens.length > 0 && (
        <div className="rounded-lg border border-border bg-card">
          <button
            type="button"
            onClick={() => setApprovalLinksOpen((o) => !o)}
            className="flex w-full items-center justify-between border-b border-border p-4 text-left hover:bg-muted/40"
            aria-expanded={approvalLinksOpen}
          >
            <p className="text-sm font-semibold">{t("pages.changeOrders.detail.approvalLinks")}</p>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${approvalLinksOpen ? "rotate-180" : ""}`}
            />
          </button>
          {approvalLinksOpen && (
            <div className="divide-y divide-border">
              {tokens.map((tk) => (
                <div
                  key={tk.id}
                  className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-start gap-3">
                    {tk.status === "approved" ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                    ) : tk.status === "rejected" ? (
                      <XCircle className="mt-0.5 h-4 w-4 text-destructive" />
                    ) : (
                      <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {tk.status === "sent"
                          ? t("pages.changeOrders.detail.awaitingResponse")
                          : t(`statuses.${tk.status}`)}
                        {tk.client_email && (
                          <span className="ml-2 font-normal text-muted-foreground">
                            · {tk.client_email}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("pages.changeOrders.detail.sentOn", {
                          date: new Date(tk.created_at).toLocaleString(safeLocale(i18n.language)),
                        })}
                        {tk.responded_at &&
                          t("pages.changeOrders.detail.respondedOn", {
                            date: new Date(tk.responded_at).toLocaleString(
                              safeLocale(i18n.language),
                            ),
                          })}
                      </p>
                      {tk.response_notes && (
                        <p className="mt-1 text-sm whitespace-pre-wrap text-muted-foreground">
                          “{tk.response_notes}”
                        </p>
                      )}
                      {tk.status === "approved" && !tk.client_signature_data && (
                        <p className="mt-1 text-xs font-medium text-destructive">
                          {t(
                            "pages.changeOrders.detail.approvedInvalidNoSignature",
                            "Aprovação inválida — sem assinatura registrada.",
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {tk.status === "approved" && tk.client_signature_data && (
                      <Button
                        variant="default"
                        size="sm"
                        className="gap-2"
                        disabled={pdfLoading}
                        onClick={openPdfPreview}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {t("pages.changeOrders.detail.viewSigned", "Ver assinado")}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => copyApprovalLink(tk.token)}
                    >
                      <Copy className="h-3.5 w-3.5" /> {t("pages.changeOrders.detail.copyLink")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      onClick={() => openAsClient(tk.token)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t("pages.changeOrders.detail.openAsClient")}
                    </Button>
                    {tk.status === "sent" && isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => revokeToken(tk.id)}>
                        {t("pages.changeOrders.detail.revoke")}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <EmailSendHistoryCard kind="change_order" changeOrderId={co.id} />

      {editing ? (
        <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="edit-title">{t("pages.changeOrders.detail.editTitleLabel")}</Label>
            <Input
              id="edit-title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-reason">{t("pages.changeOrders.detail.reason")}</Label>
            <Input
              id="edit-reason"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-tax">{t("pages.changeOrders.detail.taxPercentLabel")}</Label>
            <Input
              id="edit-tax"
              type="number"
              step="0.01"
              min="0"
              value={editTaxPercent}
              onChange={(e) => setEditTaxPercent(Number(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="edit-desc">{t("pages.changeOrders.detail.description")}</Label>
            <Textarea
              id="edit-desc"
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("pages.estimates.form.scopePhotos")}</Label>
            <ScopePhotosUploader
              bucket="change-order-scope-photos"
              companyId={co.company_id}
              photos={editScopePhotos}
              setPhotos={setEditScopePhotos}
            />
          </div>
        </div>
      ) : (
        (co.reason || co.description) && (
          <div className="space-y-3">
            {co.reason && (
              <div className="rounded-lg border border-border bg-card p-5">
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.changeOrders.detail.reason")}
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {isCustomerRequestReason(co.reason)
                    ? t("pages.changeOrders.approval.customerRequest")
                    : co.reason}
                </p>
              </div>
            )}
            {co.description && (
              <div className="rounded-lg border border-border bg-card p-5">
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.changeOrders.detail.description")}
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{co.description}</p>
              </div>
            )}
          </div>
        )
      )}

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <p className="text-sm font-semibold">{t("pages.changeOrders.detail.lineItems")}</p>
            <p className="text-xs text-muted-foreground">
              {t("pages.changeOrders.detail.itemsHint")}
            </p>
          </div>
          {editing && (
            <Button variant="outline" size="sm" onClick={addEditItem} className="gap-2">
              <Plus className="h-3.5 w-3.5" />
              {t("pages.changeOrders.detail.addItem")}
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.changeOrders.detail.description")}</TableHead>
                <TableHead className={editing ? "w-24" : "w-20 text-right"}>
                  {t("pages.changeOrders.detail.qty")}
                </TableHead>
                <TableHead className={editing ? "w-32" : "w-32 text-right"}>
                  {t("pages.changeOrders.detail.unitPrice")}
                </TableHead>
                <TableHead className="w-32 text-right">
                  {t("pages.changeOrders.detail.total")}
                </TableHead>
                {editing && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {editing
                ? editItems.map((it, idx) => {
                    const lineTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <Input
                            value={it.description}
                            onChange={(e) => updateEditItem(idx, { description: e.target.value })}
                            placeholder={t("pages.changeOrders.detail.itemPlaceholder")}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={it.quantity}
                            onChange={(e) =>
                              updateEditItem(idx, { quantity: Number(e.target.value) })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={it.unit_price}
                            onChange={(e) =>
                              updateEditItem(idx, { unit_price: Number(e.target.value) })
                            }
                          />
                        </TableCell>
                        <TableCell
                          className={`text-right text-sm font-medium ${lineTotal < 0 ? "text-rose-600" : ""}`}
                        >
                          {signedMoney(lineTotal)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeEditItem(idx)}
                            disabled={editItems.length === 1}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                : items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>{it.description}</TableCell>
                      <TableCell className="text-right">{it.quantity}</TableCell>
                      <TableCell className="text-right">{formatMoney(it.unit_price)}</TableCell>
                      <TableCell
                        className={`text-right font-medium ${it.total < 0 ? "text-rose-600" : ""}`}
                      >
                        {signedMoney(it.total)}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
          <div className="flex w-64 justify-between">
            <span className="text-muted-foreground">{t("pages.changeOrders.detail.subtotal")}</span>
            <span>{signedMoney(editing ? editSubtotal : co.subtotal)}</span>
          </div>
          <div className="flex w-64 justify-between">
            <span className="text-muted-foreground">
              {t("pages.changeOrders.detail.taxLabel", {
                percent: editing ? editTaxPercent : co.tax_percent,
              })}
            </span>
            <span>{signedMoney(editing ? editTaxAmount : co.tax_amount)}</span>
          </div>
          <div
            className={`mt-1 flex w-64 justify-between border-t border-border pt-1 text-base font-semibold ${displayTotal < 0 ? "text-rose-600" : "text-emerald-600"}`}
          >
            <span>{t("pages.changeOrders.detail.deltaTotal")}</span>
            <span>{signedMoney(displayTotal)}</span>
          </div>
        </div>
      </div>

      <div className="text-center">
        <p className="text-xs text-muted-foreground">{t("pages.changeOrders.detail.footerNote")}</p>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.changeOrders.detail.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{
                __html: t("pages.changeOrders.detail.deleteDesc", { number: co.number }),
              }}
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.changeOrders.detail.confirmResetTitle")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1 text-sm">
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("pages.changeOrders.detail.confirmResetLine1", {
                      status: t(`statuses.${co.status}`),
                    }),
                  }}
                />
                <p
                  dangerouslySetInnerHTML={{
                    __html: `• ${t("pages.changeOrders.detail.confirmResetLine2")}`,
                  }}
                />
                <p>• {t("pages.changeOrders.detail.confirmResetLine3")}</p>
                <p>• {t("pages.changeOrders.detail.confirmResetLine4")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingEdit}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => performSaveEdit(true)} disabled={savingEdit}>
              {savingEdit
                ? t("pages.changeOrders.detail.savingDots")
                : t("pages.changeOrders.detail.saveAndRevert")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.changeOrders.detail.sendDialogTitle")}</DialogTitle>
            <DialogDescription>{t("pages.changeOrders.detail.sendDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="send-email">{t("pages.changeOrders.detail.recipientEmail")}</Label>
              <Input
                id="send-email"
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder={t("pages.changeOrders.detail.recipientPlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="send-message">{t("pages.changeOrders.detail.messageOptional")}</Label>
              <Textarea
                id="send-message"
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                rows={3}
                placeholder={t("pages.changeOrders.detail.sendDialogPlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>
              {t("common.cancel")}
            </Button>
            <Button onClick={sendApprovalLink} disabled={sending} className="gap-2">
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sending
                ? t("pages.changeOrders.detail.sending")
                : t("pages.changeOrders.detail.sendLink")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        pdfBase64={pdfPreviewBase64}
        filename={pdfPreviewFilename}
        onDownload={confirmDownloadPdf}
      />
    </div>
  );
}
